import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { SectionCard } from "../ui";
import { useTaskPolling } from "../../hooks";
import { fmtDate } from "../../utils";
import type { PowerProject } from "../../types";
import { useZ7Generations, useZ7Mutations, useZ7Overview } from "./z7hooks";
import type { Z7Cell, Z7Column, Z7Overview, Z7RowData, Z7Source } from "./z7types";

// Załącznik 7 ENERGA — ewidencja zawierania zgód/tytułów prawnych.
// Grid działka×właściciel; ilości mają proweniencję (mapa DXF / projekt techniczny AI /
// ręcznie), braki są jawnie „do doprecyzowania" (zero zgadywania), plik XLSX/ODS
// generowany z wyceną liczoną FORMUŁAMI (ilość × stawka).

const SOURCE_PL: Record<Z7Source, { label: string; color: Parameters<typeof Badge>[0]["color"] }> = {
  dxf: { label: "mapa", color: "blue" },
  ai: { label: "PT (AI)", color: "purple" },
  manual: { label: "ręcznie", color: "green" },
};

function fmtQty(v: string | null | undefined, unit: string): string {
  if (v == null || v === "None") return "—";
  const n = Number(v);
  return `${Number.isInteger(n) ? n : n.toFixed(2)} ${unit === "m2" ? "m²" : unit === "szt" ? "szt." : "mb"}`;
}

function rowClarifications(row: Z7RowData): number {
  return Object.values(row.cells).filter((c) => c.needs_clarification).length;
}

// ── modal: analiza projektu technicznego (AI) ───────────────────────────────────
function AnalyzePtModal({ connectionId, overview, onClose, onDone }: {
  connectionId: string; overview: Z7Overview; onClose: () => void; onDone: () => void;
}) {
  const m = useZ7Mutations(connectionId);
  const [fileId, setFileId] = useState("");
  const [upload, setUpload] = useState<File | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const status = task.data?.status;
  const running = m.analyzePt.isPending || (!!taskId && !["success", "failure", "cancelled"].includes(status ?? ""));

  // Fire onDone exactly once at terminal status. Without the ref, an unstable onDone
  // (a fresh arrow each parent render → invalidate) would loop: invalidate → refetch →
  // render → new onDone → effect → invalidate → …
  const doneRef = useRef(false);
  useEffect(() => {
    if (taskId && (status === "success" || status === "failure") && !doneRef.current) {
      doneRef.current = true;
      onDone();
    }
  }, [taskId, status, onDone]);

  return (
    <Modal open onClose={onClose} title="Uzupełnij z projektu technicznego (AI)" size="md">
      <div className="space-y-3 text-sm">
        <p className="text-xs text-[var(--color-text-muted)]">
          AI czyta PDF projektu technicznego i uzupełnia detale, których mapa nie zna
          (ilość torów, układ słupów, izolacja, powierzchnie pod słupami, linie WN).
          Raportuje <b>wyłącznie wartości zapisane wprost w dokumencie</b> (z cytatem
          i stroną); czego nie znajdzie — oznacza „do doprecyzowania” z pytaniem.
          Wartości wpisane ręcznie nie są ruszane.
        </p>
        <label className="flex flex-col gap-1">
          <span className="section-label">Plik PT z projektu (zakładka Pliki)</span>
          <select className="input-base" value={fileId} disabled={!!upload || running}
            onChange={(e) => setFileId(e.target.value)}>
            <option value="">— wybierz PDF —</option>
            {overview.pt_files.map((f) => (
              <option key={f.id} value={f.id}>{f.name}{f.description ? ` — ${f.description}` : ""}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="section-label">…albo wgraj PDF</span>
          <input type="file" accept="application/pdf" disabled={running}
            onChange={(e) => setUpload(e.target.files?.[0] ?? null)} />
        </label>
        {taskId && (
          <div className="text-xs font-mono whitespace-pre-wrap max-h-48 overflow-auto border border-[var(--color-border)] p-2">
            {(task.data?.log as string | undefined) || `Status: ${status ?? "…"}`}
          </div>
        )}
        {status === "success" && <div className="text-xs text-green-700">✓ Analiza zakończona.</div>}
        {(status === "failure" || m.analyzePt.isError) && (
          <div className="text-xs text-[var(--color-primary)]">Analiza nie powiodła się — uzupełnij ręcznie.</div>
        )}
        <div className="flex gap-2">
          <button type="button"
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[var(--color-primary)] text-white disabled:opacity-50"
            disabled={running || (!fileId && !upload)}
            onClick={() => m.analyzePt.mutate(
              upload ? { upload } : { fileId },
              { onSuccess: (r) => setTaskId(r.task_id) },
            )}>
            {running ? "Analizuję…" : "Start analizy"}
          </button>
          <button type="button" className="text-xs px-3 py-1.5 text-[var(--color-text-muted)]" onClick={onClose}>
            Zamknij
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── modal: stawki ───────────────────────────────────────────────────────────────
function RatesModal({ connectionId, overview, onClose }: {
  connectionId: string; overview: Z7Overview; onClose: () => void;
}) {
  const m = useZ7Mutations(connectionId);
  // edytujemy tylko blok „projektowane" — bliźniacze kolumny __istn dostają tę samą stawkę
  const projCols = overview.columns.filter((c) => c.section === "projektowane");
  const [rates, setRates] = useState<Record<string, string>>(
    () => Object.fromEntries(projCols.map((c) => [c.code, c.rate])),
  );
  const save = () => {
    const payload: { code: string; rate: string }[] = [];
    for (const c of projCols) {
      payload.push({ code: c.code, rate: rates[c.code] || "0" });
      payload.push({ code: `${c.code}__istn`, rate: rates[c.code] || "0" });
    }
    m.saveRates.mutate(payload, { onSuccess: onClose });
  };
  return (
    <Modal open onClose={onClose} title="Stawki wyceny (zł za mb / szt. / m²)" size="lg">
      <p className="text-xs text-[var(--color-text-muted)] mb-2">
        Stawki z oryginalnego załącznika ENERGA — <b>nie były seedowane automatycznie</b>
        (bez oryginalnego pliku nie zgadujemy przypisania współczynników). Kolumny bez
        stawki nie liczą się do wyceny i w pliku dostają „STAWKA?”. Bloki „projektowane”
        i „istniejące” dzielą te same stawki.
      </p>
      <div className="max-h-[50vh] overflow-auto">
        <table className="w-full text-sm">
          <tbody>
            {projCols.map((c) => (
              <tr key={c.code} className="border-b border-[var(--color-border)]">
                <td className="py-1 pr-2">{c.label}</td>
                <td className="pr-2 w-28">
                  <input type="number" step="0.01" min="0" className="input-base w-24 text-xs"
                    value={rates[c.code] === "0.00" ? "" : rates[c.code]}
                    placeholder="—"
                    onChange={(e) => setRates((s) => ({ ...s, [c.code]: e.target.value }))} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2 mt-3">
        <button type="button"
          className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[var(--color-primary)] text-white disabled:opacity-50"
          disabled={m.saveRates.isPending}
          onClick={save}>Zapisz stawki</button>
        <button type="button" className="text-xs px-3 py-1.5 text-[var(--color-text-muted)]" onClick={onClose}>Anuluj</button>
      </div>
    </Modal>
  );
}

// ── drawer wiersza: ilości + rozstrzygnięcia ───────────────────────────────────
function RowModal({ connectionId, row, overview, onClose }: {
  connectionId: string; row: Z7RowData; overview: Z7Overview; onClose: () => void;
}) {
  const m = useZ7Mutations(connectionId);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [form, setForm] = useState(row.consent_form.value);
  const [amount, setAmount] = useState(row.final_amount.value ?? "");
  const [deed, setDeed] = useState(row.notary_deed);
  const [notes, setNotes] = useState(row.notes);
  const [showAll, setShowAll] = useState(false);

  const sections: [string, Z7Column[]][] = [
    ["Urządzenia PROJEKTOWANE (blok D)", overview.columns.filter((c) => c.section === "projektowane")],
    ["Urządzenia ISTNIEJĄCE poza PT (blok F)", overview.columns.filter((c) => c.section === "istniejace")],
  ];

  const saveAll = () => {
    const cells = Object.entries(edits).map(([column, value]) => ({
      column, value: value === "" ? null : value,
    }));
    // Only send fields the user actually changed — sending unchanged values (esp.
    // notary_deed_number) re-locks the consent form as manual, overrides the amount
    // and can spawn an empty access title. Compare against the row's initial values.
    const body: Record<string, unknown> = {};
    if (form !== row.consent_form.value) body.consent_form = form;
    const amtNorm = amount === "" ? null : amount;
    if (amtNorm !== (row.final_amount.value ?? null)) body.final_amount = amtNorm;
    if (deed !== row.notary_deed) body.notary_deed_number = deed;
    if (notes !== row.notes) body.notes = notes;

    const done = () => onClose();
    const patchRow = () => {
      if (Object.keys(body).length === 0) {
        done();
        return;
      }
      m.updateRow.mutate({ rowId: row.id, body }, { onSuccess: done });
    };
    if (cells.length) {
      m.updateQuantities.mutate({ rowId: row.id, cells }, { onSuccess: patchRow });
    } else {
      patchRow();
    }
  };

  const cellFor = (code: string): Z7Cell | undefined => row.cells[code];

  return (
    <Modal open onClose={onClose}
      title={`Działka ${row.parcel.teryt || row.parcel.short_number} — ${row.owner?.full_name ?? "brak właściciela"}`}
      size="lg">
      <div className="space-y-4 text-sm">
        <div className="text-xs text-[var(--color-text-muted)]">
          {row.parcel.commune} · obręb {row.parcel.obreb || "—"} · {row.parcel.street_and_number || "—"}
          · pow. {row.parcel.area_m2 ?? "—"} m² · KW {row.parcel.kw_number || "—"}
          {row.owner && <> · udział {row.owner.share || "—"}</>}
          {!row.include_qty && <> · <Badge color="gray">ilości liczone w pierwszym wierszu działki</Badge></>}
        </div>

        {sections.map(([title, cols]) => (
          <div key={title}>
            <div className="section-label mb-1">{title}</div>
            <table className="w-full text-xs">
              <tbody>
                {cols.filter((c) => showAll || cellFor(c.code) || edits[c.code] !== undefined).map((c) => {
                  const cell = cellFor(c.code);
                  const src = cell?.source ? SOURCE_PL[cell.source] : null;
                  return (
                    <tr key={c.code} className={`border-b border-[var(--color-border)] ${cell?.needs_clarification ? "bg-[color:rgba(217,119,6,0.08)]" : ""}`}>
                      <td className="py-1 pr-2">{c.label}</td>
                      <td className="pr-2 whitespace-nowrap">
                        {fmtQty(cell?.value ?? null, c.unit)}
                        {src && <span className="ml-1"><Badge color={src.color}>{src.label}</Badge></span>}
                        {cell?.needs_clarification && (
                          <span className="ml-1" title={cell?.note || "do doprecyzowania"}>
                            <Badge color="yellow">do doprecyzowania</Badge>
                          </span>
                        )}
                      </td>
                      <td className="pr-2 max-w-[220px] text-[var(--color-text-muted)] truncate" title={cell?.note}>
                        {cell?.note || ""}
                      </td>
                      <td className="w-28">
                        <input type="number" step="0.01" min="0" className="input-base w-24 text-xs"
                          placeholder="popraw…"
                          value={edits[c.code] ?? ""}
                          onChange={(e) => setEdits((s) => ({ ...s, [c.code]: e.target.value }))} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
        <button type="button" className="text-xs text-[var(--color-primary)]" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "pokaż tylko wypełnione kolumny" : "pokaż wszystkie kolumny (dodaj wartości ręcznie)"}
        </button>

        {row.valuation.items.length > 0 && (
          <div>
            <div className="section-label mb-1">Wycena własna (ilość × stawka)</div>
            {row.valuation.items.map((it) => (
              <div key={it.column} className="text-xs font-mono">{it.label}: {it.formula}</div>
            ))}
            <div className="text-xs font-mono font-bold mt-1">RAZEM: {row.valuation.total} zł</div>
            {row.valuation.unrated_columns.length > 0 && (
              <div className="text-[10px] text-amber-700">
                Kolumny bez stawki (nie liczą się do wyceny): {row.valuation.unrated_columns.join(", ")}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 border-t border-[var(--color-border)] pt-3">
          <label className="flex flex-col gap-1">
            <span className="section-label">Forma zgody (kol. G)
              {row.consent_form.source === "basis" && <Badge color="blue">z tytułu dostępu</Badge>}</span>
            <select className="input-base" value={form} onChange={(e) => setForm(e.target.value)}>
              <option value="">— do doprecyzowania —</option>
              {overview.consent_forms.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="section-label">Ostateczna kwota dla właściciela [zł] (kol. H)
              {row.final_amount.source === "access_title" && <Badge color="blue">z tytułu dostępu</Badge>}</span>
            <input type="number" step="0.01" className="input-base" value={amount}
              onChange={(e) => setAmount(e.target.value)} placeholder="do doprecyzowania" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="section-label">Nr aktu notarialnego / repertorium (zapis na tytule dostępu)</span>
            <input className="input-base" value={deed} onChange={(e) => setDeed(e.target.value)}
              disabled={!row.owner} />
          </label>
          <label className="flex flex-col gap-1">
            <span className="section-label">Uwagi / wyjaśnienia</span>
            <input className="input-base" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
        </div>

        <div className="flex gap-2">
          <button type="button"
            className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 bg-[var(--color-primary)] text-white disabled:opacity-50"
            disabled={m.updateRow.isPending || m.updateQuantities.isPending || !overview.can_mutate}
            onClick={saveAll}>Zapisz</button>
          <button type="button" className="text-xs px-3 py-1.5 text-[var(--color-text-muted)]" onClick={onClose}>Anuluj</button>
          {(m.updateRow.isError || m.updateQuantities.isError) && (
            <span className="text-xs text-[var(--color-primary)]">Nie zapisano.</span>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── zakładka główna ─────────────────────────────────────────────────────────────
export function Z7Tab({ connection }: { connection: PowerProject }) {
  const { data, isLoading } = useZ7Overview(connection.id);
  const m = useZ7Mutations(connection.id);
  const { data: generations } = useZ7Generations(connection.id);
  const [selRow, setSelRow] = useState<string | null>(null);
  const [showRates, setShowRates] = useState(false);
  const [showPt, setShowPt] = useState(false);
  const [docNumber, setDocNumber] = useState("");
  const [format, setFormat] = useState<"xlsx" | "ods">("xlsx");
  const [duplicate, setDuplicate] = useState(false);
  const [genWarning, setGenWarning] = useState<string | null>(null);
  // Selekcja wierszy do generacji — trzymamy ODZNACZONE (domyślnie puste = wszystkie
  // zaznaczone); nowe wiersze po odświeżeniu są zaznaczone z automatu.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());

  const selected = useMemo(
    () => data?.rows.find((r) => r.id === selRow) ?? null,
    [data, selRow],
  );

  if (isLoading || !data) {
    return <div className="text-sm text-[var(--color-text-muted)] mt-4">Ładowanie…</div>;
  }

  const rows = data.rows;
  const missing = data.missing;
  const allGood = missing.consent_form === 0 && missing.final_amount === 0
    && missing.owner === 0 && missing.clarifications === 0 && data.rates_missing === 0;

  const selectedIds = rows.filter((r) => !deselected.has(r.id)).map((r) => r.id);
  const selectedCount = selectedIds.length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const noneSelected = selectedCount === 0;
  const toggleRow = (id: string) =>
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectAll = () => setDeselected(new Set());
  const deselectAll = () => setDeselected(new Set(rows.map((r) => r.id)));

  return (
    <div className="space-y-4 mt-4">
      <SectionCard title="Załącznik 7 — Ewidencja zawierania zgód / tytułów prawnych (ENERGA)">
        {/* kompletność jednym zdaniem */}
        <div className="text-sm mb-2">
          {rows.length === 0 ? (
            <span>Brak wierszy — kliknij „Zbuduj wiersze”, żeby utworzyć ewidencję z działek i właścicieli projektu.</span>
          ) : allGood ? (
            <span><Badge color="green">kompletna</Badge> {rows.length} wierszy, wycena własna razem: <b>{data.valuation_total} zł</b>.</span>
          ) : (
            <span>
              {rows.length} wierszy · do doprecyzowania:{" "}
              {missing.clarifications > 0 && <Badge color="yellow">{missing.clarifications} komórek ilości</Badge>}{" "}
              {missing.consent_form > 0 && <Badge color="yellow">{missing.consent_form} form zgody</Badge>}{" "}
              {missing.final_amount > 0 && <Badge color="yellow">{missing.final_amount} kwot</Badge>}{" "}
              {missing.owner > 0 && <Badge color="red">{missing.owner} bez właściciela</Badge>}{" "}
              {data.rates_missing > 0 && (
                <span title="Kolumny bez stawki nie liczą się do wyceny — uzupełnij w „Stawkach”.">
                  <Badge color="orange">{data.rates_missing} stawek brak</Badge>
                </span>
              )}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
          <button type="button" className="px-2 py-1 border border-[var(--color-border)] font-mono"
            disabled={!data.can_mutate || m.refreshRows.isPending}
            onClick={() => m.refreshRows.mutate()}>Zbuduj wiersze</button>
          <button type="button" className="px-2 py-1 border border-[var(--color-border)] font-mono"
            disabled={!data.can_mutate || m.refreshDxf.isPending}
            title="Przelicz ilości urządzeń z mapy projektu (rozbicie elementów trasy per działka)"
            onClick={() => m.refreshDxf.mutate()}>
            {m.refreshDxf.isPending ? "Liczę…" : "Uzupełnij z mapy (DXF)"}
          </button>
          <button type="button" className="px-2 py-1 border border-[var(--color-border)] font-mono"
            disabled={!data.can_mutate}
            onClick={() => setShowPt(true)}>Uzupełnij z projektu technicznego (AI)</button>
          {data.can_edit_rates && (
            <button type="button" className="px-2 py-1 border border-[var(--color-border)] font-mono"
              onClick={() => setShowRates(true)}>Stawki</button>
          )}
          <span className="flex-1" />
          <input className="input-base text-xs w-40" placeholder="Nr dokumentu"
            value={docNumber} onChange={(e) => setDocNumber(e.target.value)} />
          <select className="input-base text-xs" value={format} onChange={(e) => setFormat(e.target.value as "xlsx" | "ods")}>
            <option value="xlsx">XLSX</option>
            <option value="ods">ODS</option>
          </select>
          <label className="inline-flex items-center gap-1"
            title="Domyślnie ilości urządzeń i wycena tylko w pierwszym wierszu działki (suma się nie dubluje przy współwłaścicielach). Zaznacz, jeśli ENERGA chce ilości w każdym wierszu.">
            <input type="checkbox" checked={duplicate} onChange={(e) => setDuplicate(e.target.checked)} />
            ilości w każdym wierszu
          </label>
          <button type="button"
            className="px-3 py-1.5 bg-[var(--color-primary)] text-white font-mono uppercase tracking-wider disabled:opacity-50"
            disabled={!data.can_mutate || m.generate.isPending || rows.length === 0 || noneSelected}
            title={noneSelected ? "Zaznacz co najmniej jedną działkę" : undefined}
            onClick={() => m.generate.mutate(
              { format, doc_number: docNumber, duplicate_quantities: duplicate, row_ids: selectedIds },
              {
                onSuccess: (r) => {
                  setGenWarning(r.warning);
                  const url = (r.file as { url?: string | null }).url;
                  if (url) window.open(url, "_blank");
                },
              },
            )}>
            {m.generate.isPending ? "Generuję…" : "Generuj plik"}
          </button>
        </div>
        {genWarning && <div className="text-xs text-amber-700 mb-2">{genWarning}</div>}
        {!data.template_available && (
          <div className="text-[10px] font-mono text-[var(--color-text-muted)] mb-2">
            Plik budowany od zera (układ wg wzoru ENERGA). Po dostarczeniu oryginalnego XLSX
            załącznika generator wypełni go 1:1.
          </div>
        )}

        {rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
            <span className="text-[var(--color-text-muted)]">
              Do wygenerowania: <b>{selectedCount}</b> z {rows.length} {rows.length === 1 ? "wiersza" : "wierszy"}
            </span>
            <button type="button" className="px-2 py-1 border border-[var(--color-border)] font-mono disabled:opacity-40"
              onClick={selectAll} disabled={allSelected}>Zaznacz wszystkie</button>
            <button type="button" className="px-2 py-1 border border-[var(--color-border)] font-mono disabled:opacity-40"
              onClick={deselectAll} disabled={noneSelected}>Odznacz wszystkie</button>
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--color-text-muted)] border-b border-[var(--color-border)]">
                  <th className="py-1.5 pr-2 w-8">
                    <input type="checkbox" aria-label="Zaznacz/odznacz wszystkie"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = !allSelected && !noneSelected; }}
                      onChange={() => (allSelected ? deselectAll() : selectAll())} />
                  </th>
                  <th className="py-1.5 pr-2">Lp.</th>
                  <th className="pr-2">Działka</th>
                  <th className="pr-2">Właściciel</th>
                  <th className="pr-2">Urządzenia (komórki)</th>
                  <th className="pr-2">Wycena własna</th>
                  <th className="pr-2">Forma zgody</th>
                  <th className="pr-2">Kwota ostateczna</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const clar = rowClarifications(r);
                  const filled = Object.values(r.cells).filter((c) => c.value != null && c.value !== "None").length;
                  return (
                    <tr key={r.id}
                      className="border-b border-[var(--color-border)] cursor-pointer hover:bg-[var(--color-bg-elevated)]"
                      onClick={() => setSelRow(r.id)}>
                      <td className="pr-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" aria-label="Zaznacz do generacji"
                          checked={!deselected.has(r.id)}
                          onChange={() => toggleRow(r.id)} />
                      </td>
                      <td className="py-1.5 pr-2 font-mono">{i + 1}</td>
                      <td className="pr-2">
                        <b>{r.parcel.teryt || r.parcel.short_number}</b>
                        <div className="text-[10px] text-[var(--color-text-muted)]">
                          {r.parcel.commune} · obręb {r.parcel.obreb || "—"} · KW {r.parcel.kw_number || "—"}
                        </div>
                      </td>
                      <td className="pr-2">
                        {r.owner ? (
                          <>
                            {r.owner.full_name}
                            <div className="text-[10px] text-[var(--color-text-muted)]">{r.owner.address || "—"}</div>
                          </>
                        ) : <Badge color="red">brak właściciela</Badge>}
                      </td>
                      <td className="pr-2">
                        {r.include_qty ? (
                          <>
                            <span className="font-mono">{filled}</span> wypełnionych
                            {clar > 0 && <span className="ml-1"><Badge color="yellow">{clar} do doprecyzowania</Badge></span>}
                          </>
                        ) : <span className="text-[10px] text-[var(--color-text-muted)]">w pierwszym wierszu działki</span>}
                      </td>
                      <td className="pr-2 font-mono">{r.valuation.total !== "0.00" ? `${r.valuation.total} zł` : "—"}</td>
                      <td className="pr-2">
                        {r.consent_form.value
                          ? <span className="text-xs">{overviewLabel(data, r.consent_form.value)}
                              {r.consent_form.source === "basis" && <span className="ml-1"><Badge color="blue">z tytułu</Badge></span>}</span>
                          : <Badge color="yellow">do doprecyzowania</Badge>}
                      </td>
                      <td className="pr-2 font-mono">
                        {r.final_amount.value != null ? `${r.final_amount.value} zł` : <Badge color="yellow">brak</Badge>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2">
              Klik w wiersz = edycja ilości i rozstrzygnięć. Kolory źródeł: mapa (DXF) / PT (AI) / ręcznie —
              ręczne zawsze wygrywa; AI nie nadpisuje ręcznych.
            </p>
          </div>
        )}
      </SectionCard>

      {(generations ?? []).length > 0 && (
        <SectionCard title="Wygenerowane pliki">
          <table className="w-full text-sm">
            <tbody>
              {(generations ?? []).map((g) => (
                <tr key={g.id} className="border-b border-[var(--color-border)]">
                  <td className="py-1 pr-2 font-mono text-xs">{fmtDate(g.created_at)}</td>
                  <td className="pr-2">{g.doc_number || "—"} <Badge color="gray">{g.format.toUpperCase()}</Badge></td>
                  <td className="pr-2 text-xs text-[var(--color-text-muted)]">
                    {String((g.totals as { rows?: number }).rows ?? "—")} wierszy ·
                    wycena {String((g.totals as { valuation_total?: string }).valuation_total ?? "—")} zł
                  </td>
                  <td className="pr-2 text-xs">{g.generated_by ?? "—"}</td>
                  <td className="pr-2">
                    {(g.file as { url?: string | null }).url
                      ? <a className="text-[var(--color-primary)] text-xs" href={(g.file as { url?: string }).url} target="_blank" rel="noreferrer">pobierz</a>
                      : <span className="text-xs text-[var(--color-text-muted)]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      {selected && (
        <RowModal connectionId={connection.id} row={selected} overview={data} onClose={() => setSelRow(null)} />
      )}
      {showRates && <RatesModal connectionId={connection.id} overview={data} onClose={() => setShowRates(false)} />}
      {showPt && (
        <AnalyzePtModal connectionId={connection.id} overview={data}
          onClose={() => setShowPt(false)} onDone={() => m.invalidate()} />
      )}
    </div>
  );
}

function overviewLabel(data: Z7Overview, value: string): string {
  return data.consent_forms.find((f) => f.value === value)?.label ?? value;
}
